import UpgradeTrack, {
  colorByTrack,
  upgradeTracks,
  upgradeTrackToDifficulty,
} from 'app/frontend/types/custom/UpgradeTrack'
import {
  ColumnDefWithFilters,
  ColumnGroup,
  EnrichedTeamMembership,
} from '../types'
import { useSimpleColumns } from '../utils'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import CrestCapTooltip from 'app/frontend/base/components/CrestCapTooltip'
import { crestTypes } from 'app/frontend/types/custom/CrestType'
import {
  itemEquipmentSlots,
  itemSlotLabels,
  type ItemEquipmentSlot,
} from 'app/frontend/types/custom/ItemSlot'
import { wowheadUrl } from 'app/frontend/base/helpers'
import { itemQualityClasses } from 'app/frontend/types/custom/ItemQuality'
import { EquippedItem } from 'app/frontend/types/custom'
import { Fragment, ReactNode, useMemo } from 'react'
import AuditFilter, { useAuditFilterState } from '../components/AuditFilter'
import { MultiSelect } from '@/components/ui/multi-select'
import { cn } from '@/lib/utils'
import { usePageInfo } from 'app/frontend/base/config/queries'
import { useTeam } from 'app/frontend/base/config/context/team'
import { Badge } from '@/components/ui/badge'
import { crestEarnableCap } from 'app/frontend/base/helpers/crestCap'
import { gameVersionHas } from 'app/frontend/types/custom/GameVersion'
import type { SeasonVoidforged } from 'app/frontend/types/custom/Season'

const seasonTracks = new Set<string>(
  upgradeTracks.filter((track) => track !== 'None'),
)

const equippedItemsOnTrack = (
  member: EnrichedTeamMembership,
  track: UpgradeTrack | 'untracked',
) => {
  if (!member.gear) return []

  return itemEquipmentSlots.flatMap((slot) => {
    const item = member.gear?.[slot]
    if (!item?.id) return []

    const matches =
      track === 'untracked'
        ? !seasonTracks.has(item.upgrade_level?.track)
        : item.upgrade_level?.track === track

    return matches ? [{ slot, item }] : []
  })
}

const untrackedEquippedItemCount = (member: EnrichedTeamMembership) =>
  equippedItemsOnTrack(member, 'untracked').length

const venomforgedStatus = (
  member: EnrichedTeamMembership,
  voidforged: SeasonVoidforged,
) => {
  const gear = member.gear
  const twoHanded = !!gear?.main_hand?.id && !gear?.off_hand?.id
  const slots: ItemEquipmentSlot[] = [
    'neck',
    'trinket_1',
    'trinket_2',
    'main_hand',
    ...(twoHanded ? [] : (['off_hand'] as const)),
  ]
  const bonusIds = [
    ...Object.values(voidforged.bonus_ids),
    voidforged.crafted_bonus_id,
  ].filter((id): id is number => id != null)

  const items: { slot: ItemEquipmentSlot; item: EquippedItem }[] = []
  let required = slots.length

  slots.forEach((slot) => {
    const item = gear?.[slot]
    if (!item?.id) return

    if ((item.bonus_ids ?? []).some((id) => bonusIds.includes(id))) {
      items.push({ slot, item })
    } else if (item.ilvl >= voidforged.item_levels.mythic) {
      required -= 1
    }
  })

  return { items, count: items.length, required }
}

const itemLevelClass = (item: EquippedItem, colorItemLevel: boolean) => {
  if (!colorItemLevel) return 'text-foreground'

  const { step = 0, total = 0 } = item.upgrade_level ?? {}
  if ((step === 0 && total === 0) || step === total) {
    return 'text-success-foreground'
  }

  return 'text-destructive-foreground'
}

const TrackItemsTooltip = ({
  items,
  gameVersion,
  className,
  colorItemLevel = true,
  children,
}: {
  items: { slot: ItemEquipmentSlot; item: EquippedItem }[]
  gameVersion: wowaudit.PageInfo['gameVersion']
  className?: string
  colorItemLevel?: boolean
  children: ReactNode
}) => {
  if (items.length === 0) {
    return <span className={className}>{children}</span>
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={className}>{children}</span>
      </TooltipTrigger>
      <TooltipContent className='w-max text-left'>
        <div className='grid grid-cols-[auto_auto_auto] items-baseline gap-x-2.5 gap-y-1'>
          {items.map(({ slot, item }) => (
            <Fragment key={slot}>
              <span className='text-xs text-muted-foreground'>
                {itemSlotLabels[slot]}
              </span>
              <span
                className={cn(
                  'text-right tabular-nums',
                  itemLevelClass(item, colorItemLevel),
                )}
              >
                {item.ilvl}
              </span>
              <a
                href={wowheadUrl(gameVersion, item)}
                target='_blank'
                rel='noreferrer'
                className={`whitespace-nowrap ${
                  itemQualityClasses[item.quality || 0]
                }`}
              >
                {item.name}
              </a>
            </Fragment>
          ))}
        </div>
      </TooltipContent>
    </Tooltip>
  )
}

export const useGearUpgradesColumns = (): ColumnDefWithFilters => {
  const { value: enabledTracks, setValue: setEnabledTracks } =
    useAuditFilterState<UpgradeTrack[]>('upgradeTracks', [], upgradeTracks)

  const { guild } = useTeam()
  const {
    data: { currentPeriod, currentSeason, gameVersion },
  } = usePageInfo()
  const period = currentPeriod[guild?.realm.region ?? 'EU']
  const voidforged = currentSeason.voidforged

  const collectionGroups: ColumnGroup[] = useMemo(
    () => [
      {
        id: 'items_by_track',
        header: 'Equipped items by track',
        rankFields: null,
        subColumns: [
          ...upgradeTracks
            .filter((track) => track !== 'None')
            .map((track) => ({
              id: track as string,
              header: () => (
                <Tooltip>
                  <TooltipTrigger>{track.charAt(0)}</TooltipTrigger>
                  <TooltipContent>{track} track</TooltipContent>
                </Tooltip>
              ),
              field: `${upgradeTrackToDifficulty[track]}_track_items`,
              cellWrapper: (
                value: string | number,
                member: EnrichedTeamMembership,
              ) =>
                value === '-' || value === '' || value == null ? (
                  ''
                ) : (
                  <TrackItemsTooltip
                    items={equippedItemsOnTrack(member, track)}
                    gameVersion={gameVersion}
                    className={colorByTrack[track]}
                  >
                    {value}
                  </TrackItemsTooltip>
                ),
            })),
          {
            id: 'untracked_items',
            header: () => (
              <Tooltip>
                <TooltipTrigger>-</TooltipTrigger>
                <TooltipContent>Not on a current season track</TooltipContent>
              </Tooltip>
            ),
            numeric: true,
            accessor: untrackedEquippedItemCount,
            cellWrapper: (value: string | number, member) =>
              Number(value) > 0 ? (
                <TrackItemsTooltip
                  items={equippedItemsOnTrack(member, 'untracked')}
                  gameVersion={gameVersion}
                  colorItemLevel={false}
                >
                  {value}
                </TrackItemsTooltip>
              ) : (
                ''
              ),
          },
        ],
      },
      ...(voidforged?.enabled
        ? [
            {
              id: 'venomforged_items',
              header: `${voidforged.label}`,
              rankFields: null,
              subColumns: [
                {
                  id: 'venomforged_count',
                  header: 'Equipped',
                  numeric: true,
                  accessor: (member: EnrichedTeamMembership) =>
                    venomforgedStatus(member, voidforged).count,
                  cellWrapper: (
                    _value: string | number,
                    member: EnrichedTeamMembership,
                  ) => {
                    if (!member.gear) return ''

                    const { items, count, required } = venomforgedStatus(
                      member,
                      voidforged,
                    )

                    return (
                      <TrackItemsTooltip
                        items={items}
                        gameVersion={gameVersion}
                        colorItemLevel={false}
                        className={
                          required > 0 && count === 0
                            ? 'text-destructive-foreground'
                            : count < required
                            ? 'text-warning-foreground'
                            : 'text-success-foreground'
                        }
                      >
                        {count} / {required}
                      </TrackItemsTooltip>
                    )
                  },
                },
              ],
            },
          ]
        : []),
      ...(gameVersionHas('crests', gameVersion)
        ? ([
            {
              id: 'crests_earned',
              header: 'Crests earned',
              rankFields: [
                'myth_crests',
                'hero_crests',
                'champion_crests',
                'veteran_crests',
              ],
              rankWeights: {
                myth_crests: 1000,
                hero_crests: 10,
                champion_crests: 2,
                veteran_crests: 1,
              },
              subColumns: crestTypes.map((crest) => {
                const seasonCrest = currentSeason.crests.find(
                  (c) => c.name === crest,
                )
                const showCap =
                  !currentSeason.crests_uncapped &&
                  !!seasonCrest?.uncapped_crests &&
                  Object.keys(seasonCrest.uncapped_crests).length > 0
                const cap = seasonCrest
                  ? crestEarnableCap(seasonCrest, period)
                  : 0

                return {
                  id: `${crest}_crests`,
                  header: showCap
                    ? () => (
                        <span className='flex flex-col items-center gap-1 pb-1'>
                          <span>{crest}</span>
                          <CrestCapTooltip crest={seasonCrest} period={period}>
                            <Badge
                              variant='outline'
                              className='px-1.5 py-0 font-normal whitespace-nowrap'
                            >
                              Cap: {cap}
                            </Badge>
                          </CrestCapTooltip>
                        </span>
                      )
                    : crest,
                  field: `${crest.toLowerCase()}_crests`,
                  cellWrapper: (value, member) => {
                    if (value === '-' || value === '' || value == null) {
                      return <span>{value}</span>
                    }

                    const earned = Number(value)
                    const earnedSources =
                      member.uncappedCrests?.[String(currentSeason.id)]?.[
                        crest
                      ] ?? {}
                    const className =
                      showCap && earned >= cap
                        ? 'text-success-foreground'
                        : showCap
                        ? 'text-warning-foreground'
                        : undefined

                    if (
                      currentSeason.crests_uncapped ||
                      !seasonCrest ||
                      earned >= cap
                    ) {
                      return <span className={className}>{value}</span>
                    }

                    return (
                      <CrestCapTooltip
                        crest={seasonCrest}
                        period={period}
                        earned={earned}
                        earnedSources={earnedSources}
                        className={cn('whitespace-nowrap', className)}
                      >
                        {value}
                      </CrestCapTooltip>
                    )
                  },
                }
              }),
            },
          ] as ColumnGroup[])
        : []),
      {
        id: 'upgrades_missing',
        header: 'Upgrades',
        rankInverted: true,
        subColumns: [
          {
            id: 'total_upgrades_missing',
            header: 'Missing',
            field: 'total_upgrades_missing',
          },
        ],
      },
      {
        id: 'upgrades_by_slot',
        header: 'Upgrade steps',
        rankFields: null,
        subColumns: itemEquipmentSlots.map((slot) => ({
          id: slot as string,
          header: () => <span>{itemSlotLabels[slot]}</span>,
          field: `upgrade_level_${slot}`,
          cellWrapper: (value, member) => {
            const [step, total] = String(value).split('/').map(Number)
            const track = member.gear?.[slot]?.upgrade_level?.track

            const valueToShow =
              enabledTracks.length === 0
                ? value
                : enabledTracks.includes(track as UpgradeTrack)
                ? value
                : ''

            return (
              <Tooltip>
                <TooltipTrigger
                  className={cn(
                    'whitespace-nowrap',
                    valueToShow === '-'
                      ? ''
                      : step === total
                      ? 'text-success-foreground'
                      : 'text-destructive-foreground',
                  )}
                >
                  {valueToShow}
                </TooltipTrigger>
                {track === 'None' ? null : (
                  <TooltipContent>
                    {track ? (
                      <span className={colorByTrack[track]}>{track}</span>
                    ) : (
                      'Unknown track'
                    )}
                  </TooltipContent>
                )}
              </Tooltip>
            )
          },
        })),
      },
    ],
    [currentSeason, currentPeriod, enabledTracks, gameVersion, period],
  )

  const columns = useSimpleColumns(collectionGroups, [enabledTracks])

  return {
    columns,
    initialSortBy: [{ id: 'Myth_crests', desc: true }],
    filters: [
      <AuditFilter label='Count missing steps from' key='track'>
        <MultiSelect<UpgradeTrack>
          options={upgradeTracks.slice(0, 4).map((track) => ({
            value: track,
            label: <span className={colorByTrack[track]}>{track}</span>,
          }))}
          placeholder='All tracks'
          value={enabledTracks}
          onChange={(value) => setEnabledTracks(value)}
        />
      </AuditFilter>,
    ],
  }
}
